import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Shield, Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';
import SeoMeta from '../components/SeoMeta';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Token manquant');
      return;
    }
    
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await api.get(`/auth/verify-reset-token?token=${token}`);
      setValidToken(true);
      setEmail(response.data.email);
    } catch (err) {
      setError(err.response?.data?.detail || 'Token invalide ou expiré');
      setValidToken(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      });
      
      setSuccess(true);
      toast.success('Mot de passe modifié avec succès !');
      
      // Rediriger vers login après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <SeoMeta title="Réinitialisation du mot de passe" description="Réinitialisez votre mot de passe Drone Maps." noindex />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-sky-500 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/30">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-white font-[Barlow]">Drone Maps</span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {loading ? (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="w-10 h-10 text-sky-500 animate-spin mb-4" />
              <p className="text-slate-600">Vérification du lien...</p>
            </div>
          ) : success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Mot de passe modifié !</h2>
              <p className="text-slate-600 mb-6">
                Votre mot de passe a été réinitialisé avec succès.
              </p>
              <p className="text-sm text-slate-500">
                Redirection vers la page de connexion...
              </p>
            </div>
          ) : !validToken ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Lien invalide</h2>
              <p className="text-slate-600 mb-6">
                {error || 'Ce lien de réinitialisation est invalide ou a expiré.'}
              </p>
              <Link to="/login">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white">
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-7 h-7 text-sky-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Nouveau mot de passe</h2>
                <p className="text-slate-600 mt-2">
                  Choisissez un nouveau mot de passe pour <span className="font-medium">{email}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Confirmer le mot de passe
                  </label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>

                <p className="text-xs text-slate-500">
                  Le mot de passe doit contenir au moins 6 caractères.
                </p>

                <Button
                  type="submit"
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Modification...
                    </>
                  ) : (
                    'Modifier le mot de passe'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-slate-400 text-sm mt-6">
          <Link to="/login" className="hover:text-white transition-colors">
            Retour à la connexion
          </Link>
        </p>
      </div>
    </div>
  );
}
